import { scrollbarGap } from '@/components/Organizer/shared/sidePanel.utils'

/**
 * One counted body scroll lock shared by every overlay (drawers, side panels, dialogs). Each
 * overlay used to remember the previous `overflow` and hand it back on unmount — fine when they
 * close one at a time, wrong when two unmount in the same React commit: the cleanups run in tree
 * order, so a drawer restored '' and the confirmation opened above it restored 'hidden' and the
 * page stayed frozen (William, 27 Oct 2026: planning page stuck after the first workout). Here the
 * original styles are saved when the count goes 0 → 1 and restored when it comes back to 0,
 * whatever the order. `className` (e.g. `org-drawer-open`) is counted on its own.
 *
 * `getTarget()` → { style, classList, gap }: the real body below, a fake one in the tests.
 */
export function createScrollLock(getTarget) {
    let count = 0
    let saved = null
    const classes = new Map()

    const lock = ({ className } = {}) => {
        const target = getTarget()
        count += 1
        if (count === 1) {
            saved = { overflow: target.style.overflow, paddingRight: target.style.paddingRight }
            target.style.overflow = 'hidden'
            // the right padding replaces the scrollbar so the page does not shift (0 with overlay scrollbars)
            if (target.gap) target.style.paddingRight = `${target.gap}px`
        }
        if (className) {
            const n = (classes.get(className) || 0) + 1
            classes.set(className, n)
            if (n === 1) target.classList.add(className)
        }
    }

    const unlock = ({ className } = {}) => {
        const target = getTarget()
        if (count > 0) {
            count -= 1
            if (count === 0 && saved) {
                target.style.overflow = saved.overflow
                target.style.paddingRight = saved.paddingRight
                saved = null
            }
        }
        if (className && classes.has(className)) {
            const n = classes.get(className) - 1
            if (n <= 0) {
                classes.delete(className)
                target.classList.remove(className)
            } else classes.set(className, n)
        }
    }

    return { lock, unlock, get count() { return count } }
}

export const bodyScrollLock = createScrollLock(() => ({
    style: document.body.style,
    classList: document.body.classList,
    gap: scrollbarGap(window.innerWidth, document.documentElement.clientWidth),
}))
